import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import '../index.css';
import logo from '../assets/spinner.gif';
import { performPasswordChange, UseLocalStorage, GetAttributesByAssignment } from '../functions';

function UserMaint() {
    const [localStorageAttributes, setLocalStorageAttributes] = UseLocalStorage('attributes', []);
    
    const navigate = useNavigate();
    const [modalIsOpen, setIsOpen] = React.useState(false);
    const [modalText, setModalText] = React.useState("Error changing password");
    const [modalType, setModalType] = React.useState("error"); // "success" or "error"
    const [loading, setLoading] = useState(false);

    function openModal(success) {
        setModalType(success ? "success" : "error");
        setIsOpen(true);

        setTimeout(() => {
            closeModal();
        }, 2800);
    }

    function closeModal() {
        setIsOpen(false);
    }

    const [formData, setFormData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    const userAttributes = GetAttributesByAssignment('users', localStorageAttributes);
    let pageTitle = "User Maintenance";

    const cookies = new Cookies();
    let login = cookies.get('login');

    if (!login) {
        return (<Navigate to={{ pathname: '../Login' }} />);
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
            setModalText('All fields are required');
            openModal(false);
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            setModalText('New passwords do not match');
            openModal(false);
            return;
        }

        if (formData.newPassword.length < 6) {
            setModalText('New password must be at least 6 characters long');
            openModal(false);
            return;
        }

        if (formData.currentPassword === formData.newPassword) {
            setModalText('New password must be different from current password');
            openModal(false);
            return;
        }

        setLoading(true);

        try {
            const success = await performPasswordChange(formData.currentPassword, formData.newPassword);

            if (success) {
                setModalText('Password changed successfully!');
                setFormData({
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: ''
                });
                setLoading(false);
                openModal(true);
            } else {
                setModalText('Failed to change password. Please check your current password.');
                setLoading(false);
                openModal(false);
            }
        } catch (error) {
            console.error('Password change error:', error);
            setModalText('An error occurred while changing password');
            setLoading(false);
            openModal(false);
        }
    };

    return (
        <>
            <div className="UserMaint">
                <h1>{pageTitle}</h1>

                {/* Success/Error Modal */}
                {modalIsOpen && (
                    <div className="custom-modal-overlay" onClick={closeModal}>
                        <div
                            className={`custom-modal ${modalType === "success" ? "success" : "error"}`}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="modal-icon">
                                {modalType === "success" ? (
                                    <div className="checkmark-circle">
                                        <div className="checkmark"></div>
                                    </div>
                                ) : (
                                    <div className="error-circle">
                                        <div className="error-x"></div>
                                    </div>
                                )}
                            </div>
                            <h2 className="modal-title">
                                {modalType === "success" ? "Success!" : "Error!"}
                            </h2>
                            <p className="modal-message">{modalText}</p>
                            <div className="modal-progress-bar">
                                <div className="modal-progress-fill"></div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="user-form-container" style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <form className="user-form" onSubmit={handleSubmit}>
                        <div className="form-section" style={{ backgroundColor: '#1e1e1e', padding: '30px', borderRadius: '8px', boxShadow: '0 2px 5px rgba(0, 0, 0, 0.5)' }}>
                            <h3 style={{ marginTop: '0', marginBottom: '25px', textAlign: 'center' }}>Change Password</h3>
                            
                            <div className="form-row" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label htmlFor="currentPassword" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Current Password</label>
                                    <input
                                        type="password"
                                        id="currentPassword"
                                        name="currentPassword"
                                        value={formData.currentPassword}
                                        onChange={handleInputChange}
                                        required
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div className="form-row" style={{ marginBottom: '20px' }}>
                                <div className="form-group">
                                    <label htmlFor="newPassword" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>New Password</label>
                                    <input
                                        type="password"
                                        id="newPassword"
                                        name="newPassword"
                                        value={formData.newPassword}
                                        onChange={handleInputChange}
                                        required
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div className="form-row" style={{ marginBottom: '25px' }}>
                                <div className="form-group">
                                    <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Confirm New Password</label>
                                    <input
                                        type="password"
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        value={formData.confirmPassword}
                                        onChange={handleInputChange}
                                        required
                                        style={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </div>
                            </div>

                            <div className="form-submit" style={{ textAlign: 'center' }}>
                                <button 
                                    type="submit" 
                                    disabled={loading}
                                    style={{ 
                                        minWidth: '150px', 
                                        padding: '12px 24px',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        margin: '0 auto'
                                    }}
                                >
                                    {loading ? (
                                        <>
                                            <img src={logo} style={{ width: '16px', marginRight: '8px' }} alt="loading..." />
                                            Changing Password...
                                        </>
                                    ) : 'Change Password'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default UserMaint;